import { Link } from 'react-router-dom'
import { Code, MessageSquare, Zap, Shield, X, ExternalLink, Send, AlertCircle, CheckCircle2, Sparkles, Plug, Library, ShieldCheck, Gavel, Server, BookOpen } from 'lucide-react'
import { Testimonials } from '../components/SocialProof'
import { useTranslation } from 'react-i18next'
import { useState, lazy, Suspense } from 'react'

// react-markdown is heavy and only needed once the live demo produces an
// assistant answer — keep it out of the landing page's critical bundle (LCP)
const ChatMarkdown = lazy(() => import('../components/ChatMarkdown'))
import SEO from '../components/SEO'
import { SEO_CONFIGS } from '../utils/seo'

export default function HomePage() {
  const { t, i18n } = useTranslation()

  // Chat interface state
  const [chatQuery, setChatQuery] = useState('')
  const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string, sources?: any[], article_links?: any[], metadata?: any}>>([])
  const [isChatLoading, setIsChatLoading] = useState(false)
  const [chatError, setChatError] = useState('')
  const [selectedArticleLink, setSelectedArticleLink] = useState<any | null>(null)
  const [queriesRemaining, setQueriesRemaining] = useState<number | null>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!chatQuery.trim()) return

    // Add user message to chat
    const userMessage = { role: 'user' as const, content: chatQuery }
    const currentQuery = chatQuery
    setChatMessages(prev => [...prev, userMessage])
    setChatQuery('')
    setIsChatLoading(true)
    setChatError('')

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: currentQuery,
          conversation_history: chatMessages,
          language: i18n.language  // Pass current language (it, en, es)
        })
      })

      if (response.status === 429) {
        // Rate limit exceeded
        const data = await response.json()
        setShowUpgradeModal(true)
        setChatError(data.detail?.message || 'Rate limit exceeded. Please create an account to continue.')
        // Remove the user message
        setChatMessages(prev => prev.slice(0, -1))
        setIsChatLoading(false)
        return
      }

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`)
      }

      if (!response.body) {
        throw new Error('Response body is null')
      }

      // Initialize streaming assistant message
      const assistantMessageIndex = chatMessages.length + 1
      setChatMessages(prev => [...prev, {
        role: 'assistant' as const,
        content: '',
        sources: [],
        article_links: [],
        metadata: {}
      }])

      // Read SSE stream
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))

              // Support both legacy public chat stream events (content/content_final)
              // and unified /chat/stream events (chunk/done).
              if (data.type === 'content' || data.type === 'chunk') {
                // Append content chunk to assistant message
                setChatMessages(prev => {
                  const updated = [...prev]
                  updated[assistantMessageIndex] = {
                    ...updated[assistantMessageIndex],
                    content: updated[assistantMessageIndex].content + (data.content || '')
                  }
                  return updated
                })
              } else if (data.type === 'content_final') {
                // Replace entire content with normalized version (handles multilingual article citations)
                setChatMessages(prev => {
                  const updated = [...prev]
                  updated[assistantMessageIndex] = {
                    ...updated[assistantMessageIndex],
                    content: data.content
                  }
                  return updated
                })
              } else if (data.type === 'article_link') {
                // Collect article links as they arrive
                setChatMessages(prev => {
                  const updated = [...prev]
                  const currentLinks = updated[assistantMessageIndex].article_links || []
                  updated[assistantMessageIndex] = {
                    ...updated[assistantMessageIndex],
                    article_links: [...currentLinks, data.article_link]
                  }
                  return updated
                })
              } else if (data.type === 'done') {
                // Update metadata, article_links, and queries remaining
                if (data.metadata) {
                  console.log('[Chat Debug] Response metadata:', data.metadata)

                  setChatMessages(prev => {
                    const updated = [...prev]
                    updated[assistantMessageIndex] = {
                      ...updated[assistantMessageIndex],
                      metadata: data.metadata,
                      // Keep existing article_links from streaming events
                      article_links: updated[assistantMessageIndex].article_links || data.metadata.article_links || []
                    }
                    return updated
                  })

                  if (data.metadata.queries_remaining !== undefined) {
                    setQueriesRemaining(data.metadata.queries_remaining)
                  }
                }
              } else if (data.type === 'error') {
                throw new Error(data.error || data.message || 'Unknown streaming error')
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', parseError)
            }
          }
        }
      }
    } catch (err) {
      setChatError(err instanceof Error ? err.message : 'Failed to get response')
      // Remove the user message on error
      setChatMessages(prev => prev.slice(0, -1))
    } finally {
      setIsChatLoading(false)
    }
  }

  return (
    <div>
      {/* SEO Meta Tags — LegalService (org entity), WebSite (sitelinks
          search), SoftwareApplication. No aggregateRating: self-serving
          review markup without real collected reviews violates Google's
          structured-data policy. */}
      <SEO
        {...SEO_CONFIGS.home}
        url="/"
        structuredData={[
          { type: 'LegalService', id: 'organization' },
          { type: 'WebSite', id: 'website' },
          { type: 'SoftwareApplication', id: 'software-app' },
          {
            type: 'FAQPage',
            id: 'home-faq',
            data: {
              questions: [
                {
                  question: "Cos'e il MCP e perche mi serve?",
                  answer:
                    "Il Model Context Protocol (MCP) e lo standard aperto con cui qualsiasi LLM (Claude Desktop, GPT, Cursor, agenti custom) puo interrogare i nostri dati legali italiani senza scrivere una riga di codice API. Tu configuri l'MCP, l'LLM lo scopre da solo.",
                },
                {
                  question: "I miei dati personali vengono inviati all'LLM?",
                  answer:
                    "No. Il nostro servizio di redazione PII rileva e maschera 10 categorie (codice fiscale, IBAN, email, telefono, partita IVA, ecc.) prima che la richiesta raggiunga l'LLM. Ogni redazione e registrata in un log immutabile.",
                },
              ],
            },
          },
        ]}
      />

      {/* Hero Section with Live Chat */}
      <section className="container mx-auto px-4 py-12 md:py-20">
        {/* Title spanning full width */}
        <h1 className="text-4xl md:text-6xl font-bold mb-8 text-center text-foreground">
          {t('home.hero.title')}
        </h1>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Left Column: Live Chat Interface */}
          <div>
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-6 border-2 border-primary/20">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-bold">{t('home.chatInterface.tryNow')}</h3>
              </div>

              {/* Chat Messages */}
              <div className="bg-background rounded-lg p-4 mb-4 min-h-[350px] max-h-[450px] overflow-y-auto shadow-inner">
                {chatMessages.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium mb-2">{t('nav.startConversation')}</p>
                    <p className="text-xs">{t('home.chatInterface.askAnything')}</p>
                    <div className="mt-4 text-xs text-left space-y-1 bg-muted/50 rounded p-3">
                      <p className="font-semibold mb-1">{t('home.chatInterface.examples')}</p>
                      <p className="opacity-75">• {t('home.chatInterface.saleQuestion')}</p>
                      <p className="opacity-75">• {t('home.chatInterface.article2043')}</p>
                      <p className="opacity-75">• {t('home.chatInterface.tenantRights')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chatMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-lg p-3 ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-foreground'
                          }`}
                        >
                          {/* Render assistant content as markdown with clickable article links */}
                          {msg.role === 'assistant' ? (
                            <Suspense
                              fallback={<p className="text-sm whitespace-pre-wrap">{msg.content}</p>}
                            >
                              <ChatMarkdown
                                content={msg.content}
                                articleLinks={msg.article_links}
                                onArticleClick={setSelectedArticleLink}
                              />
                            </Suspense>
                          ) : (
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          )}


                          {msg.sources && msg.sources.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-border/50">
                              <p className="text-xs opacity-75 mb-1">Fonti:</p>
                              {msg.sources.slice(0, 2).map((source: any, i: number) => (
                                <p key={i} className="text-xs opacity-75">
                                  • {source.descrizione || source.title}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {isChatLoading && (
                      <div className="flex justify-start">
                        <div className="bg-muted text-foreground rounded-lg p-3">
                          <div className="flex items-center gap-2 text-sm">
                            <div className="animate-pulse">{t('home.chatInterface.thinking')}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Query Counter */}
              {queriesRemaining !== null && (
                <div className="mb-3 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    {queriesRemaining > 0 ? (
                      <>
                        <span className="text-primary font-semibold">{queriesRemaining}</span> {t('home.chatInterface.freeQuestionsRemaining')}
                      </>
                    ) : (
                      <span className="text-destructive">{t('home.chatInterface.limitReached')}</span>
                    )}
                  </p>
                </div>
              )}

              {/* Chat Input */}
              <form onSubmit={handleChatSubmit}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatQuery}
                    onChange={(e) => setChatQuery(e.target.value)}
                    placeholder={t('home.chatInterface.chatPlaceholder')}
                    className="flex-1 px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                    disabled={isChatLoading}
                  />
                  <button
                    type="submit"
                    disabled={isChatLoading || !chatQuery.trim()}
                    className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>

              {chatError && (
                <div className="bg-destructive/10 text-destructive px-3 py-2 rounded-md mt-3 text-sm">
                  {chatError}
                </div>
              )}

              <p className="text-xs text-muted-foreground mt-3 text-center">
                {t('home.hero.demoCaption')}
              </p>
            </div>
          </div>

          {/* Right Column: Marketing Content */}
          <div className="text-center md:text-left">
            <p className="text-lg md:text-2xl text-muted-foreground mb-2 font-semibold">
              {t('home.hero.valueProp')}
            </p>
            <p className="text-base text-muted-foreground/80 mb-8">
              {t('home.hero.valuePropSub')}
            </p>

            {/* What You Get */}
            <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                {t('home.chatInterface.whatYouGet')}
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>{t('home.chatInterface.immediateResponses')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>{t('home.chatInterface.preciseCitations')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>{t('home.chatInterface.directAccess')}</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Link
                to="/register"
                className="bg-primary text-primary-foreground px-8 py-3 rounded-md font-medium hover:bg-primary/90 min-h-touch transition-colors"
              >
                {t('home.hero.cta')}
              </Link>
              <Link
                to="/pricing"
                className="border border-input px-8 py-3 rounded-md font-medium hover:bg-accent min-h-touch transition-colors"
              >
                {t('common.seePrices')}
              </Link>
            </div>

            {/* ChatGPT Comparison Table */}
            <div className="bg-muted border border-border rounded-xl p-6 mb-6 max-w-2xl mx-auto md:mx-0">
              <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {t('home.chatInterface.betterThanChatGPT')}
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                {t('home.chatInterface.officialTexts')}
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-destructive mb-2 flex items-center gap-1">
                    <X className="w-4 h-4" />
                    {t('home.chatInterface.genericChatGPT')}
                  </p>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    <li>• {t('home.chatInterface.notSpecialized')}</li>
                    <li>• {t('home.chatInterface.usServer')}</li>
                    <li>• {t('home.chatInterface.genericTraining')}</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-green-600 mb-2 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    {t('home.chatInterface.normattivaAI')}
                  </p>
                  <ul className="space-y-1 text-xs">
                    <li>• {t('home.chatInterface.officialTexts')}</li>
                    <li>• {t('home.chatInterface.italianCodeOnly')}</li>
                    <li>• {t('home.chatInterface.euServer')}</li>
                    <li>• {t('home.chatInterface.italianLawTraining')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Founder's Circle Urgency Banner */}
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 border-2 border-amber-300 rounded-xl p-6 mb-6 shadow-lg max-w-2xl mx-auto md:mx-0">
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-base font-bold text-amber-900 mb-1">
                    {t('home.chatInterface.launchOffer')}
                  </p>
                  <p className="text-sm text-amber-800 mb-2">
                    {t('home.chatInterface.first50', {
                      interpolation: { escapeValue: false }
                    })}
                  </p>
                  <p className="text-xs text-amber-700 mb-3">
                    €49/mese → <span className="text-lg font-bold text-amber-900">€34/mese</span> per sempre
                  </p>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="bg-amber-200 rounded-full h-2 flex-1 overflow-hidden">
                      <div className="bg-amber-600 h-full" style={{width: '76%'}}></div>
                    </div>
                    <span className="text-xs font-semibold text-amber-900">38 {t('home.chatInterface.spotsRemaining')}</span>
                  </div>
                  <Link to="/register" className="inline-flex items-center gap-1 text-sm font-semibold text-amber-900 hover:text-amber-950 underline">
                    {t('home.chatInterface.requestDiscount')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badge Strip — platform primitives at a glance */}
      <section className="border-y border-border bg-background/50">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            <div className="flex items-center gap-3">
              <Plug className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold leading-tight">{t('home.platformStrip.mcp.title')}</p>
                <p className="text-xs text-muted-foreground leading-tight">{t('home.platformStrip.mcp.sub')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Library className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold leading-tight">{t('home.platformStrip.codes.title')}</p>
                <p className="text-xs text-muted-foreground leading-tight">{t('home.platformStrip.codes.sub')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold leading-tight">{t('home.platformStrip.pii.title')}</p>
                <p className="text-xs text-muted-foreground leading-tight">{t('home.platformStrip.pii.sub')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Gavel className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold leading-tight">{t('home.platformStrip.massime.title')}</p>
                <p className="text-xs text-muted-foreground leading-tight">{t('home.platformStrip.massime.sub')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Server className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold leading-tight">{t('home.platformStrip.server.title')}</p>
                <p className="text-xs text-muted-foreground leading-tight">{t('home.platformStrip.server.sub')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Piattaforma agentica — 4 cards explaining the infrastructure */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('home.platform.title')}</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">{t('home.platform.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Card 1 — MCP */}
          <div className="bg-background border border-border rounded-2xl p-6 md:p-8">
            <Plug className="w-10 h-10 text-primary mb-4" aria-hidden="true" />
            <h3 className="text-xl font-bold mb-3">{t('home.platform.cards.mcp.title')}</h3>
            <p className="text-muted-foreground mb-4">{t('home.platform.cards.mcp.body')}</p>
            <a href="#how-it-works" className="text-primary font-medium hover:underline">
              {t('home.platform.cards.mcp.cta')}
            </a>
          </div>
          {/* Card 2 — Local client */}
          <div className="bg-background border border-border rounded-2xl p-6 md:p-8">
            <Code className="w-10 h-10 text-primary mb-4" aria-hidden="true" />
            <h3 className="text-xl font-bold mb-3">{t('home.platform.cards.local.title')}</h3>
            <p className="text-muted-foreground mb-4">{t('home.platform.cards.local.body')}</p>
            <a href="#how-it-works" className="text-primary font-medium hover:underline">
              {t('home.platform.cards.local.cta')}
            </a>
          </div>
          {/* Card 3 — PII redaction */}
          <div className="bg-background border border-border rounded-2xl p-6 md:p-8">
            <ShieldCheck className="w-10 h-10 text-primary mb-4" aria-hidden="true" />
            <h3 className="text-xl font-bold mb-3">{t('home.platform.cards.pii.title')}</h3>
            <p className="text-muted-foreground mb-4">{t('home.platform.cards.pii.body')}</p>
            <a href="#trust" className="text-primary font-medium hover:underline">
              {t('home.platform.cards.pii.cta')}
            </a>
          </div>
          {/* Card 4 — Massime */}
          <div className="bg-background border border-border rounded-2xl p-6 md:p-8">
            <Gavel className="w-10 h-10 text-primary mb-4" aria-hidden="true" />
            <h3 className="text-xl font-bold mb-3">{t('home.platform.cards.massime.title')}</h3>
            <p className="text-muted-foreground mb-4">{t('home.platform.cards.massime.body')}</p>
            <Link to="/massime" className="text-primary font-medium hover:underline">
              {t('home.platform.cards.massime.cta')}
            </Link>
          </div>
        </div>
      </section>

      {/* Cosa vedi tu — static visual proof of the user-visible output */}
      <section id="what-you-see" className="bg-muted py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('home.demo.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">{t('home.demo.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Col 1 — Codice */}
            <div className="bg-background rounded-2xl border border-border p-6">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-5 h-5 text-primary" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase text-muted-foreground">Codice</span>
              </div>
              <p className="text-sm font-semibold mb-2">{t('home.demo.codice.header')}</p>
              <p className="text-sm text-muted-foreground italic mb-4">"{t('home.demo.codice.excerpt')}"</p>
              <a
                href="https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:regio.decreto:1942-03-16;262:2;2043"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary font-medium hover:underline inline-flex items-center gap-1"
              >
                {t('home.demo.codice.linkLabel')}<ExternalLink className="w-3 h-3" aria-hidden="true" />
              </a>
            </div>
            {/* Col 2 — Massima */}
            <div className="bg-background rounded-2xl border border-border p-6">
              <div className="flex items-center gap-2 mb-3">
                <Gavel className="w-5 h-5 text-primary" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase text-muted-foreground">Massima</span>
              </div>
              <p className="text-sm font-semibold mb-2">{t('home.demo.massima.header')}</p>
              <p className="text-sm text-muted-foreground italic mb-4">"{t('home.demo.massima.excerpt')}"</p>
              <Link
                to={`/massima/${encodeURIComponent('288041-01')}`}
                className="text-sm text-primary font-medium hover:underline inline-flex items-center gap-1"
              >
                {t('home.demo.massima.linkLabel')}<ExternalLink className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
            {/* Col 3 — Risposta LLM */}
            <div className="bg-primary/5 border-2 border-primary/30 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-primary" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase text-muted-foreground">Risposta LLM</span>
              </div>
              <p className="text-sm text-foreground leading-relaxed">{t('home.demo.risposta.text')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-muted py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">{t('home.features.title')}</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-background p-8 rounded-lg">
              <Code className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-2xl font-bold mb-4">{t('home.features.feature1.title')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('home.features.feature1.description')}
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  <span>{t('home.features.feature1.searchByReference')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  <span>{t('home.features.feature1.fullTextSearch')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  <span>{t('home.features.feature1.structureNavigation')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-background p-8 rounded-lg">
              <MessageSquare className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-2xl font-bold mb-4">{t('home.features.feature2.title')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('home.features.feature2.description')}
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  <span>{t('home.features.feature2.naturalLanguage')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  <span>{t('home.features.feature2.legalAnalysis')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  <span>{t('home.features.feature2.properCitations')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section — 3 tiles that mirror the badge strip */}
      <section id="trust" className="container mx-auto px-4 py-20">
        <div className="flex items-center justify-center gap-4 mb-12">
          <Shield className="w-8 h-8 text-primary" aria-hidden="true" />
          <h2 className="text-3xl font-bold">{t('trust.title')}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <div>
            <h3 className="text-xl font-semibold mb-2">{t('home.trust.tile1.title')}</h3>
            <p className="text-muted-foreground">
              {t('home.chatInterface.officialData')}
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">{t('home.trust.tile2.title')}</h3>
            <p className="text-muted-foreground">
              {t('home.chatInterface.responsesInclude')}
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">{t('home.trust.tile3.title')}</h3>
            <p className="text-muted-foreground">
              {t('home.chatInterface.euData')}
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Continua a imparare — article teasers */}
      <section className="bg-muted py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('home.continueLearning.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('home.continueLearning.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-8">
            {(['leggereSentenza', 'massimaSentenza', 'quandoServeAvvocato'] as const).map((id) => (
              <Link
                key={id}
                to={`/guide/${id === 'leggereSentenza' ? 'leggere-una-sentenza' : id === 'massimaSentenza' ? 'massima-e-sentenza' : 'quando-serve-avvocato'}`}
                className="block bg-background border border-border rounded-2xl p-6 hover:border-primary transition-colors"
              >
                <h3 className="text-lg font-bold mb-2">{t(`home.continueLearning.teasers.${id}.title`)}</h3>
                <p className="text-sm text-muted-foreground">{t(`home.continueLearning.teasers.${id}.description`)}</p>
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/guide" className="text-primary font-medium hover:underline">{t('home.continueLearning.viewAll')}</Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-primary-foreground py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('home.cta.title')}</h2>
          <p className="text-xl mb-8 opacity-90">
            {t('home.cta.subtitle')}
          </p>
          <Link
            to="/register"
            className="bg-background text-foreground px-8 py-3 rounded-md font-medium hover:bg-background/90 inline-block"
          >
            {t('home.cta.button')}
          </Link>
        </div>
      </section>

      {/* Upgrade Modal (Rate Limit) */}
      {showUpgradeModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            onClick={() => setShowUpgradeModal(false)}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
              {/* Close button */}
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-amber-600" />
                </div>

                <h2 className="text-2xl font-bold mb-2">{t('upgradeModal.title')}</h2>
                <p className="text-gray-600 mb-6">
                  {t('upgradeModal.description')}
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm font-semibold text-blue-900 mb-2">{t('upgradeModal.freeFeaturesTitle')}</p>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span>{t('upgradeModal.unlimitedQuestions')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span>{t('upgradeModal.savedConversations')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span>{t('upgradeModal.allDevices')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span>{t('upgradeModal.fullCitations')}</span>
                    </li>
                  </ul>
                </div>

                <div className="flex flex-col gap-3">
                  <Link
                    to="/register"
                    className="bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors"
                  >
                    {t('upgradeModal.createFreeAccount')}
                  </Link>
                  <Link
                    to="/login"
                    className="text-gray-600 hover:text-gray-900 text-sm"
                  >
                    {t('upgradeModal.alreadyHaveAccount')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Article Iframe Modal */}
      {selectedArticleLink && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            onClick={() => setSelectedArticleLink(null)}
          />

          {/* Panel */}
          <div className="fixed inset-y-0 right-0 w-full md:w-2/3 lg:w-1/2 bg-white shadow-2xl z-50 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex-1 min-w-0 mr-4">
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {selectedArticleLink.display}
                </h2>
                {selectedArticleLink.riferimento && (
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedArticleLink.riferimento}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Open in New Tab */}
                <a
                  href={selectedArticleLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Apri in una nuova scheda"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>

                {/* Close Button */}
                <button
                  onClick={() => setSelectedArticleLink(null)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Chiudi (Esc)"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Iframe Content */}
            <div className="flex-1 overflow-hidden relative">
              <iframe
                src={selectedArticleLink.url}
                title={selectedArticleLink.display}
                className="w-full h-full border-0"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            </div>

            {/* Footer Info */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-600 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                {t('home.chatInterface.providedBy')}{' '}
                <a
                  href="https://www.normattiva.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline font-medium"
                >
                  Normattiva.it
                </a>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
